import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { flowsReportApi } from '@/features/reports/services';
import { DateGrouping } from '@/features/reports/models';
import { useBaseReport } from '@/features/reports/util/useBaseReport';

export function useFlowReport(reportType: string) {
  const store = useStore();

  const {
    reportId,
    data,
    chartType,
    isDataLoaded,
    loadingError,
    isDataEmpty,
    currentWorkspace,
    selectedDateOption,
    showLineChart,
    showPercentageBarChart,
    showBarChart,
    renderReportData,
    getSeriesData,
    switchLineChart,
    switchStackedChart,
    switchBarChart,
  } = useBaseReport(reportType, loadReportData);

  // Flow-specific properties
  const selectedFlowId = computed<string>(() => store.getters.selectedFlowId);

  // Watcher for flow ID changes
  watch(selectedFlowId, async (newFlowId: string, previousFlowId: string) => {
    if (newFlowId && newFlowId !== previousFlowId) {
      await renderReportData();
    }
  });

  function isReportCriterionValid() {
    return !!selectedDateOption.value && !!selectedFlowId.value;
  }

  async function loadReportData() {
    const filterRange = selectedDateOption.value.getDateRange();

    return flowsReportApi.get(currentWorkspace.value.id, {
      type: reportType,
      flowId: getFlowIdForRequest(),
      startDate: filterRange.start,
      endDate: filterRange.end,
      grouping: dataPointGrouping(),
    });
  }

  function getFlowIdForRequest(): string {
    return selectedFlowId.value;
  }

  function dataPointGrouping(): DateGrouping {
    return selectedDateOption.value.group;
  }

  return {
    reportId,
    data,
    chartType,
    isDataLoaded,
    isDataEmpty,
    loadingError,
    showLineChart,
    showPercentageBarChart,
    showBarChart,
    renderReportData,
    selectedFlowId,
    getFlowIdForRequest,
    getSeriesData,
    dataPointGrouping,
    switchLineChart,
    switchStackedChart,
    switchBarChart,
  };
}
